import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { WHATSAPP_NUMBER, CONTACT_EMAIL } from "@/lib/api";
import { PROPERTY_LIST } from "@/lib/properties";

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer
      id="contacto"
      data-testid="site-footer"
      className="bg-stone-900 text-stone-200 pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Link to="/" className="font-serif-display text-3xl md:text-4xl mb-4 inline-block">
            Villas <span className="text-[#E07A5F]">Pura Vida</span>
          </Link>
          <p className="font-serif-display italic text-xl text-stone-400 mb-6 mt-2">
            {t("footer.tagline")}
          </p>
          <p className="text-sm text-stone-400 leading-relaxed max-w-md">
            {t("footer.seo")}
          </p>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-eyebrow text-[#F2CC8F] mb-5">{t("footer.villas")}</h4>
          <ul className="space-y-3 text-sm">
            {PROPERTY_LIST.map((p) => (
              <li key={p.id}>
                <Link to={`/villas/${p.slug}`} className="hover:text-[#E07A5F] transition-colors">
                  {p.name}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/blog" className="hover:text-[#E07A5F] transition-colors">Blog</Link>
              </li>
              <li>
              <Link to="/" className="hover:text-[#E07A5F] transition-colors">
                {t("nav.home")}
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-eyebrow text-[#F2CC8F] mb-5">{t("footer.contact")}</h4>
          <ul className="space-y-3 text-sm">
            <li>
              <a
                data-testid="footer-whatsapp"
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-3 hover:text-[#E07A5F] transition-colors"
              >
                <i className="fa-brands fa-whatsapp text-lg" />
                +521 7711290073
              </a>
            </li>
            <li>
              <a
                data-testid="footer-email"
                href={`mailto:${CONTACT_EMAIL}`}
                className="inline-flex items-center gap-3 hover:text-[#E07A5F] transition-colors"
              >
                <i className="fa-solid fa-envelope text-lg" />
                {CONTACT_EMAIL}
              </a>
            </li>
            <li className="inline-flex items-start gap-3 text-stone-400">
              <i className="fa-solid fa-location-dot text-lg mt-0.5" />
              <span>
                Playa Norte, 92773<br />
                Barra de Galindo, Tuxpan, Veracruz, MX<br />
                <span className="text-xs text-stone-500">Plus Code: 3JMQ+8Q Laja, Veracruz</span>
              </span>
            </li>
          </ul>

          {/* Social media — 3 canales activos */}
          <div className="mt-6">
            <p className="text-eyebrow text-[#F2CC8F] mb-3 text-[11px]">Síguenos</p>
            <div className="flex items-center gap-3">
              <a
                data-testid="footer-instagram"
                href="https://www.instagram.com/villaspuravida.tuxpan/"
                target="_blank"
                rel="noreferrer"
                aria-label="Villas Pura Vida en Instagram"
                className="w-10 h-10 inline-flex items-center justify-center border border-stone-700 hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors"
              >
                <i className="fa-brands fa-instagram text-base" />
              </a>
              <a
                data-testid="footer-facebook"
                href="https://www.facebook.com/villaspuravidatuxpan"
                target="_blank"
                rel="noreferrer"
                aria-label="Villas Pura Vida en Facebook"
                className="w-10 h-10 inline-flex items-center justify-center border border-stone-700 hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors"
              >
                <i className="fa-brands fa-facebook-f text-base" />
              </a>
              <a
                data-testid="footer-tiktok"
                href="https://www.tiktok.com/@villa.pura.vida"
                target="_blank"
                rel="noreferrer"
                aria-label="Villas Pura Vida en TikTok"
                className="w-10 h-10 inline-flex items-center justify-center border border-stone-700 hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors"
              >
                <i className="fa-brands fa-tiktok text-base" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-16 pt-8 border-t border-stone-800 flex flex-col md:flex-row gap-4 justify-between text-xs text-stone-500">
        <p>© {year} Villas Pura Vida. {t("footer.rights")}</p>
        <div className="flex flex-wrap gap-4 items-center">
          <Link to="/aviso-de-privacidad" data-testid="footer-privacy" className="hover:text-stone-200 transition-colors">Aviso de Privacidad</Link>
          <span className="text-stone-700">·</span>
          <Link to="/condiciones-de-uso" data-testid="footer-terms" className="hover:text-stone-200 transition-colors">Condiciones de Uso</Link>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-4 text-xs text-stone-600">
        <p className="max-w-3xl">
          {t("footer.seo_title")} — Hospedaje Tuxpan Veracruz orilla del mar · casa tuxpan veracruz orilla del mar · hotel tuxpan veracruz · departamento tuxpan veracruz · loft tuxpan veracruz.
        </p>
      </div>
    </footer>
  );
}
